import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, Switch, Text, TextInput, TouchableOpacity, View } from 'react-native';
import type { SettingsScreenPalette } from '../theme/appearanceTokens';

export interface NotificationChannelValues {
  readonly maturityEnabled: boolean;
  readonly autocallEnabled: boolean;
  readonly issuerCallEnabled: boolean;
  readonly couponEnabled: boolean;
  readonly barrierHitEnabled: boolean;
  readonly sameDayEnabled: boolean;
  readonly oneDayEnabled: boolean;
  readonly sevenDaysEnabled: boolean;
  readonly customEnabled: boolean;
  readonly customDays: number;
}

type TimingSelectionMode = 'exclusiveHorizon' | 'exactDays';

interface ChannelDraft extends NotificationChannelValues {
  readonly masterEnabled: boolean;
}

interface NotificationChannelCardProps {
  readonly title: string;
  readonly palette: SettingsScreenPalette;
  readonly masterEnabled: boolean;
  readonly values: NotificationChannelValues;
  readonly timingMode: TimingSelectionMode;
  readonly onApply: (payload: { readonly masterEnabled: boolean; readonly values: NotificationChannelValues }) => void;
}

interface EventTypeOption {
  readonly key: keyof Pick<
    NotificationChannelValues,
    'maturityEnabled' | 'autocallEnabled' | 'issuerCallEnabled' | 'couponEnabled' | 'barrierHitEnabled'
  >;
  readonly label: string;
  readonly hint?: string;
}

const EVENT_TYPE_OPTIONS: readonly EventTypeOption[] = [
  { key: 'maturityEnabled', label: 'Maturity' },
  { key: 'autocallEnabled', label: 'Autocall' },
  { key: 'issuerCallEnabled', label: 'Issuer call' },
  { key: 'couponEnabled', label: 'Coupon' },
  { key: 'barrierHitEnabled', label: 'Barrier hit', hint: 'Only 1 day after' },
];

const areChannelValuesEqual = (left: NotificationChannelValues, right: NotificationChannelValues): boolean =>
  left.maturityEnabled === right.maturityEnabled &&
  left.autocallEnabled === right.autocallEnabled &&
  left.issuerCallEnabled === right.issuerCallEnabled &&
  left.couponEnabled === right.couponEnabled &&
  left.barrierHitEnabled === right.barrierHitEnabled &&
  left.sameDayEnabled === right.sameDayEnabled &&
  left.oneDayEnabled === right.oneDayEnabled &&
  left.sevenDaysEnabled === right.sevenDaysEnabled &&
  left.customEnabled === right.customEnabled &&
  left.customDays === right.customDays;

export const NotificationChannelCard: React.FC<NotificationChannelCardProps> = ({
  title,
  palette,
  masterEnabled,
  values,
  timingMode,
  onApply,
}) => {
  const styles = createStyles(palette);
  const [draft, setDraft] = useState<ChannelDraft>({ masterEnabled, ...values });
  const [customDaysValue, setCustomDaysValue] = useState(String(values.customDays));
  const isExclusiveHorizon = timingMode === 'exclusiveHorizon';
  const sectionDisabled = !draft.masterEnabled;

  useEffect(() => {
    setDraft({ masterEnabled, ...values });
    setCustomDaysValue(String(values.customDays));
  }, [
    masterEnabled,
    values.maturityEnabled,
    values.autocallEnabled,
    values.issuerCallEnabled,
    values.couponEnabled,
    values.barrierHitEnabled,
    values.sameDayEnabled,
    values.oneDayEnabled,
    values.sevenDaysEnabled,
    values.customEnabled,
    values.customDays,
  ]);

  const parseCustomDays = (rawValue: string): number | null => {
    if (!rawValue.trim()) {
      return null;
    }
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    return Math.max(0, Math.min(365, Math.floor(parsed)));
  };

  const parsedCustomDays = parseCustomDays(customDaysValue);
  const hasValidCustomDays = !draft.customEnabled || parsedCustomDays !== null;

  const isDirty = useMemo(() => {
    if (draft.masterEnabled !== masterEnabled) {
      return true;
    }
    if (customDaysValue.trim() !== String(values.customDays)) {
      return true;
    }
    return !areChannelValuesEqual(draft, values);
  }, [customDaysValue, draft, masterEnabled, values]);

  const selectedHorizon: 'sameDay' | 'oneDay' | 'sevenDays' | 'custom' | null = draft.customEnabled
    ? 'custom'
    : draft.sevenDaysEnabled
      ? 'sevenDays'
      : draft.oneDayEnabled
        ? 'oneDay'
        : draft.sameDayEnabled
          ? 'sameDay'
          : null;

  const updateDraft = (patch: Partial<ChannelDraft>): void => {
    setDraft(previous => ({ ...previous, ...patch }));
  };

  const selectHorizon = (horizon: 'sameDay' | 'oneDay' | 'sevenDays' | 'custom'): void => {
    updateDraft({
      sameDayEnabled: horizon === 'sameDay',
      oneDayEnabled: horizon === 'oneDay',
      sevenDaysEnabled: horizon === 'sevenDays',
      customEnabled: horizon === 'custom',
    });
  };

  const handleApply = (): void => {
    if (!isDirty || !hasValidCustomDays) {
      return;
    }

    const nextCustomDays = parsedCustomDays ?? draft.customDays;
    const nextValues: NotificationChannelValues = {
      maturityEnabled: draft.maturityEnabled,
      autocallEnabled: draft.autocallEnabled,
      issuerCallEnabled: draft.issuerCallEnabled,
      couponEnabled: draft.couponEnabled,
      barrierHitEnabled: draft.barrierHitEnabled,
      sameDayEnabled: draft.sameDayEnabled,
      oneDayEnabled: draft.oneDayEnabled,
      sevenDaysEnabled: draft.sevenDaysEnabled,
      customEnabled: draft.customEnabled,
      customDays: nextCustomDays,
    };

    setCustomDaysValue(String(nextCustomDays));
    setDraft({ masterEnabled: draft.masterEnabled, ...nextValues });
    onApply({
      masterEnabled: draft.masterEnabled,
      values: nextValues,
    });
  };

  const renderCheckbox = (
    label: string,
    checked: boolean,
    onPress: () => void,
    hint?: string
  ): React.ReactElement => (
    <TouchableOpacity
      style={[styles.checkboxRow, styles.nestedRow]}
      activeOpacity={0.8}
      onPress={onPress}
      disabled={sectionDisabled}
    >
      <View style={[styles.checkboxBox, checked && styles.checkboxBoxChecked]}>
        {checked ? <Text style={styles.checkboxMark}>✓</Text> : null}
      </View>
      <View style={styles.checkboxTextWrap}>
        <Text style={styles.checkboxLabel}>{label}</Text>
        {hint ? <Text style={styles.hint}>{hint}</Text> : null}
      </View>
    </TouchableOpacity>
  );

  const renderRadio = (label: string, selected: boolean, onPress: () => void): React.ReactElement => (
    <TouchableOpacity
      style={[styles.checkboxRow, styles.nestedRow]}
      activeOpacity={0.8}
      onPress={onPress}
      disabled={sectionDisabled}
    >
      <View style={[styles.radioOuter, selected && styles.radioOuterSelected]}>
        {selected ? <View style={styles.radioInner} /> : null}
      </View>
      <Text style={styles.checkboxLabel}>{label}</Text>
    </TouchableOpacity>
  );

  return (
    <View style={styles.card}>
      <View style={styles.cardHeaderRow}>
        <Text style={styles.cardHeaderTitle}>{title}</Text>
        <Switch
          value={draft.masterEnabled}
          onValueChange={value => updateDraft({ masterEnabled: value })}
          trackColor={{ false: '#D1D5DB', true: '#BFDBFE' }}
          thumbColor={draft.masterEnabled ? '#2563EB' : '#FFFFFF'}
        />
      </View>
      <View style={styles.cardDivider} />
      <View
        style={[styles.body, sectionDisabled && styles.sectionDisabled]}
        pointerEvents={sectionDisabled ? 'none' : 'auto'}
      >
        <Text style={styles.groupTitle}>Notify me about:</Text>
        {EVENT_TYPE_OPTIONS.map(option =>
          renderCheckbox(option.label, draft[option.key], () => updateDraft({ [option.key]: !draft[option.key] }), option.hint)
        )}

        <Text style={styles.groupTitle}>Notify me when:</Text>
        {isExclusiveHorizon ? (
          <>
            {renderRadio('On the same day', selectedHorizon === 'sameDay', () => selectHorizon('sameDay'))}
            {renderRadio('Next 1 day', selectedHorizon === 'oneDay', () => selectHorizon('oneDay'))}
            {renderRadio('Next 7 days', selectedHorizon === 'sevenDays', () => selectHorizon('sevenDays'))}
            <View style={[styles.checkboxRow, styles.nestedRow]}>
              <TouchableOpacity
                style={styles.checkboxContent}
                activeOpacity={0.8}
                onPress={() => selectHorizon('custom')}
                disabled={sectionDisabled}
              >
                <View style={[styles.radioOuter, selectedHorizon === 'custom' && styles.radioOuterSelected]}>
                  {selectedHorizon === 'custom' ? <View style={styles.radioInner} /> : null}
                </View>
                <Text style={styles.checkboxLabel}>Custom range (days)</Text>
              </TouchableOpacity>
              {selectedHorizon === 'custom' && (
                <TextInput
                  value={customDaysValue}
                  keyboardType="number-pad"
                  editable={draft.masterEnabled}
                  style={styles.customDaysInput}
                  placeholder="30"
                  placeholderTextColor="#9CA3AF"
                  onChangeText={value => setCustomDaysValue(value.replace(/[^0-9]/g, ''))}
                />
              )}
            </View>
          </>
        ) : (
          <>
            {renderCheckbox('On the same day', draft.sameDayEnabled, () =>
              updateDraft({ sameDayEnabled: !draft.sameDayEnabled })
            )}
            {renderCheckbox('1 day before', draft.oneDayEnabled, () =>
              updateDraft({ oneDayEnabled: !draft.oneDayEnabled })
            )}
            {renderCheckbox('7 days before', draft.sevenDaysEnabled, () =>
              updateDraft({ sevenDaysEnabled: !draft.sevenDaysEnabled })
            )}
            <View style={[styles.checkboxRow, styles.nestedRow]}>
              <TouchableOpacity
                style={styles.checkboxContent}
                activeOpacity={0.8}
                onPress={() => updateDraft({ customEnabled: !draft.customEnabled })}
                disabled={sectionDisabled}
              >
                <View style={[styles.checkboxBox, draft.customEnabled && styles.checkboxBoxChecked]}>
                  {draft.customEnabled ? <Text style={styles.checkboxMark}>✓</Text> : null}
                </View>
                <Text style={styles.checkboxLabel}>Custom range (days)</Text>
              </TouchableOpacity>
              {draft.customEnabled && (
                <TextInput
                  value={customDaysValue}
                  keyboardType="number-pad"
                  editable={draft.masterEnabled}
                  style={styles.customDaysInput}
                  placeholder="30"
                  placeholderTextColor="#9CA3AF"
                  onChangeText={value => setCustomDaysValue(value.replace(/[^0-9]/g, ''))}
                />
              )}
            </View>
          </>
        )}
      </View>
      <View style={styles.footer}>
        {isDirty ? (
          <Text style={styles.hint}>Changes are not saved until you press Apply.</Text>
        ) : null}
        <TouchableOpacity
          style={[styles.applyButton, (!isDirty || !hasValidCustomDays) && styles.applyButtonDisabled]}
          activeOpacity={0.8}
          onPress={handleApply}
          disabled={!isDirty || !hasValidCustomDays}
        >
          <Text style={styles.applyButtonText}>Apply</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const createStyles = (p: SettingsScreenPalette) =>
  StyleSheet.create({
    card: {
      width: '100%',
      backgroundColor: p.cardBackground,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: p.cardBorder,
      marginBottom: 16,
      overflow: 'hidden',
      shadowColor: p.cardShadowColor,
      shadowOpacity: p.cardShadowOpacity,
      shadowRadius: 20,
      shadowOffset: { width: 0, height: 4 },
      elevation: 8,
    },
    cardHeaderRow: {
      paddingHorizontal: 16,
      paddingVertical: 12,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    cardHeaderTitle: {
      fontSize: 16,
      color: p.cardHeaderTitle,
      fontFamily: 'Montserrat_600SemiBold',
    },
    cardDivider: {
      height: 1,
      backgroundColor: p.divider,
    },
    body: {
      paddingHorizontal: 16,
      paddingTop: 12,
      paddingBottom: 8,
    },
    sectionDisabled: {
      opacity: 0.5,
    },
    groupTitle: {
      fontSize: 13,
      color: p.cardHint,
      fontFamily: 'Montserrat_600SemiBold',
      marginTop: 10,
      marginBottom: 4,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    checkboxRow: {
      minHeight: 38,
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 6,
    },
    nestedRow: {
      marginLeft: 8,
    },
    checkboxContent: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      minWidth: 0,
    },
    checkboxBox: {
      width: 18,
      height: 18,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: p.selectorBorder,
      backgroundColor: p.selectorBackground,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 10,
    },
    checkboxBoxChecked: {
      backgroundColor: '#2563EB',
      borderColor: '#2563EB',
    },
    radioOuter: {
      width: 18,
      height: 18,
      borderRadius: 9,
      borderWidth: 1,
      borderColor: p.selectorBorder,
      backgroundColor: p.selectorBackground,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 10,
    },
    radioOuterSelected: {
      borderColor: '#2563EB',
    },
    radioInner: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: '#2563EB',
    },
    checkboxMark: {
      color: '#FFFFFF',
      fontSize: 11,
      fontFamily: 'Montserrat_700Bold',
      lineHeight: 12,
    },
    checkboxTextWrap: {
      flex: 1,
    },
    checkboxLabel: {
      fontSize: 14,
      color: p.readOnlyValue,
      fontFamily: 'Montserrat_500Medium',
    },
    hint: {
      fontSize: 11,
      color: p.cardHint,
      fontFamily: 'Montserrat_500Medium',
      marginTop: 2,
      marginBottom: 8,
    },
    customDaysInput: {
      minWidth: 72,
      height: 38,
      borderWidth: 1,
      borderColor: p.selectorBorder,
      borderRadius: 8,
      backgroundColor: p.selectorBackground,
      color: p.selectorValue,
      fontSize: 14,
      fontFamily: 'Montserrat_500Medium',
      textAlign: 'center',
      paddingHorizontal: 8,
      paddingVertical: 6,
      marginLeft: 8,
    },
    footer: {
      paddingHorizontal: 16,
      paddingBottom: 16,
      alignItems: 'flex-start',
    },
    applyButton: {
      height: 38,
      paddingHorizontal: 16,
      borderRadius: 8,
      backgroundColor: '#2563EB',
      alignItems: 'center',
      justifyContent: 'center',
    },
    applyButtonDisabled: {
      backgroundColor: '#93C5FD',
    },
    applyButtonText: {
      color: '#FFFFFF',
      fontSize: 13,
      fontFamily: 'Montserrat_600SemiBold',
    },
  });
